import {Col, Row, Skeleton, Tooltip} from 'antd';
import dayjs from 'dayjs';
import {useContext, useEffect, useMemo} from 'react';

import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../../provider/main-filter/main-filter-type';
import {SystemContext} from '../../../../../../provider/system/system-context';
import {SystemContextType} from '../../../../../../provider/system/system-context-type';
import {classNames} from '../../../../../../util/css';
import {useSummaryStats} from '../competitors-page-hook';
import {PositionChangeEnum} from '../competitors-page-type';

import {getStatisticCardsConfig} from './statistics-cards-helper';
import {StatisticsCardType} from './statistics-cards-type';
import * as styles from './statistic-cards.scss';

type PropsType = {
    positionChange: PositionChangeEnum | null;
    onPositionChangeCardClick: (positionChange: PositionChangeEnum) => void;
};

export function StatisticsCards(props: PropsType): JSX.Element {
    const {positionChange, onPositionChangeCardClick} = props;
    const systemContext = useContext<SystemContextType>(SystemContext);
    const {screen} = systemContext;
    const {isMobile} = screen;

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const {getLocalizedString} = useLocale();

    const {mutate: fetchSummary, data, isLoading: isDataInProgress, isError} = useSummaryStats();

    function getTooltipTitle(deltaValue: number | null) {
        if (!deltaValue) {
            return '';
        }

        if (deltaValue > 0) {
            return getLocalizedString('REVIEWS_ANALYSIS__COMPETITORS__TOOLTIP_TITLE', {
                count: deltaValue.toString(),
            });
        }

        return getLocalizedString('REVIEWS_ANALYSIS__COMPETITORS__TOOLTIP_TITLE_NEGATIVE', {
            count: deltaValue.toString().slice(1),
        });
    }

    useEffect(() => {
        fetchSummary({
            ...(mainFilterKey ? {filterKey: mainFilterKey} : {}),
            currentDate: dayjs().format('YYYY-MM-DD'),
            comparedDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        });
    }, [fetchSummary, mainFilterKey]);

    const statisticsCards: Array<StatisticsCardType> = useMemo<
        Array<StatisticsCardType>
    >((): Array<StatisticsCardType> => {
        return getStatisticCardsConfig({
            data,
            positionChange,
            onPositionChangeCardClick,
        });
    }, [data, onPositionChangeCardClick, positionChange]);

    return (
        <div className={styles.MainStatisticsSingle}>
            {!isError && (
                <Row className={styles.MainStatisticsSingle_row} gutter={[24, 24]} wrap>
                    {statisticsCards.map((card: StatisticsCardType): JSX.Element => {
                        return (
                            <Tooltip
                                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                key={card.langKey}
                                title={getTooltipTitle(card.deltaValue)}
                                trigger="hover"
                            >
                                <Col
                                    className={classNames(styles.MainStatisticsSingle_column, {
                                        [styles.MainStatisticsSingle_column_columnMobile]: isMobile,
                                    })}
                                    key={card.langKey}
                                    onClick={() => card.onClick?.()}
                                    span={isMobile ? 24 : 8}
                                >
                                    <div className={classNames(card.className, styles.MainStatisticsSingle_card)}>
                                        <label className={styles.MainStatisticsSingle_cardLabel}>
                                            <Locale stringKey={card.langKey} />

                                            <output className={styles.MainStatisticsSingle_cardValue}>
                                                {isDataInProgress ? (
                                                    <Skeleton.Button active />
                                                ) : (
                                                    <>
                                                        {card.value} {card.delta}
                                                    </>
                                                )}
                                            </output>
                                        </label>
                                    </div>
                                </Col>
                            </Tooltip>
                        );
                    })}
                </Row>
            )}
        </div>
    );
}
